import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Link2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'

import { InlineProductChip } from './components/InlineProductChip'
import { Badge } from 'src/shared/components/ui/badge'
import { cn } from 'src/shared/components/ui/utils'
import { assistantOnboarding } from 'src/shared/hooks/useOnboarding'
import { useLocalization } from 'src/shared/hooks/useLocalization'

// Match [product]['Name', 'id', 'color_id'] - supports straight/curly quotes and optional spacing
const PRODUCT_PATTERNS = [
  /\[product\]\s*\[\s*['"]([^'"]*)['"],\s*['"]?(\d+)['"]?,\s*['"]?(\d+)['"]?\s*\]/g,
  /\[product\]\s*\[\s*[\u2018\u2019]([^\u2018\u2019]*)[\u2018\u2019],\s*[\u2018\u2019]?(\d+)[\u2018\u2019]?,\s*[\u2018\u2019]?(\d+)[\u2018\u2019]?\s*\]/g,
]

type MessageSegment =
  | { type: 'text'; content: string }
  | {
      type: 'product'
      name: string
      product_id: number
      product_variant_color_id: number
    }

type ProductHit = {
  name: string
  product_id: number
  product_variant_color_id: number
  note: string
  segmentIdx: number
}

type MessageBlock =
  | { type: 'mixed'; segments: Array<MessageSegment & { idx: number }> }
  | { type: 'table'; products: ProductHit[]; kitSection?: boolean }

const isKitSectionHeader = (text: string) =>
  /(?:^|\n)\s*#{1,3}\s*Kit\s*#\d+/i.test(text) ||
  /(?:^|\n)\s*\*\*Kit\s*#\d+/i.test(text)

function parseMessage(message: string): MessageSegment[] {
  const segments: MessageSegment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  for (const pattern of PRODUCT_PATTERNS) {
    const re = new RegExp(pattern.source, 'g')
    const matches: Array<{ index: number; length: number; groups: string[] }> =
      []
    while ((match = re.exec(message)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        groups: [match[1], match[2], match[3]],
      })
    }
    if (matches.length > 0) {
      for (const m of matches) {
        if (m.index > lastIndex) {
          segments.push({
            type: 'text',
            content: message.slice(lastIndex, m.index),
          })
        }
        segments.push({
          type: 'product',
          name: m.groups[0],
          product_id: Number(m.groups[1]),
          product_variant_color_id: Number(m.groups[2]),
        })
        lastIndex = m.index + m.length
      }
      if (lastIndex < message.length) {
        segments.push({ type: 'text', content: message.slice(lastIndex) })
      }
      return segments
    }
  }

  if (message.length > 0) {
    segments.push({ type: 'text', content: message })
  }
  return segments
}

const isClusterGlue = (text: string) => {
  if (isKitSectionHeader(text)) return false
  const compact = text.replace(/\s+/g, ' ').trim()
  if (!compact) return true
  if (compact.length > 80) return false
  if (/\n\s*\n/.test(text)) return false
  if (/\.\s+\S/.test(compact)) return false
  return true
}

const glueNote = (text: string) =>
  text
    .replace(/\s+/g, ' ')
    .replace(/^[\s,;:!?\-–—…•*]+/, '')
    .replace(/[\s,;:!?\-–—…]+$/, '')
    .replace(/^(and|or|plus|och|samt)$/i, '')
    .replace(/^(and|or|plus|och|samt)\s+/i, '')
    .replace(/\s+(and|or|plus|och|samt)$/i, '')
    .trim()

const stripLeadingListGlue = (text: string) =>
  text
    .replace(/^[\s,;:!?\-–—…•*]+/, '')
    .replace(/^(and|or|plus|och|samt)\b[\s,;:!?\-–—…]*/i, '')
    .replace(/^\.\s*/, '')

function groupBlocksWithKitSections(segments: MessageSegment[]): MessageBlock[] {
  const blocks: MessageBlock[] = []
  let mixed: Extract<MessageBlock, { type: 'mixed' }> = {
    type: 'mixed',
    segments: [],
  }
  let i = 0
  let lastWasKitHeader = false

  const flushMixed = () => {
    if (mixed.segments.length === 0) return
    blocks.push(mixed)
    mixed = { type: 'mixed', segments: [] }
  }

  while (i < segments.length) {
    const seg = segments[i]
    if (seg.type === 'text') {
      if (isKitSectionHeader(seg.content)) {
        flushMixed()
        mixed.segments.push({ ...seg, idx: i })
        flushMixed()
        lastWasKitHeader = true
        i += 1
        continue
      }
      mixed.segments.push({ ...seg, idx: i })
      i += 1
      continue
    }

    const products: ProductHit[] = [
      {
        name: seg.name,
        product_id: seg.product_id,
        product_variant_color_id: seg.product_variant_color_id,
        note: '',
        segmentIdx: i,
      },
    ]
    let j = i + 1
    while (j < segments.length) {
      const next = segments[j]
      if (next.type === 'product') {
        products.push({
          name: next.name,
          product_id: next.product_id,
          product_variant_color_id: next.product_variant_color_id,
          note: '',
          segmentIdx: j,
        })
        j += 1
        continue
      }
      if (
        j + 1 < segments.length &&
        segments[j + 1].type === 'product' &&
        isClusterGlue(next.content)
      ) {
        products[products.length - 1].note = glueNote(next.content)
        j += 1
        continue
      }
      break
    }

    if (products.length >= 1) {
      flushMixed()
      blocks.push({ type: 'table', products, kitSection: lastWasKitHeader })
      lastWasKitHeader = false
      i = j
      const trailing = i < segments.length ? segments[i] : undefined
      if (trailing?.type === 'text') {
        const stripped = stripLeadingListGlue(trailing.content)
        if (stripped.trim()) {
          mixed.segments.push({
            type: 'text',
            content: stripped,
            idx: i,
          })
        }
        i += 1
      }
      continue
    }

    mixed.segments.push({ ...seg, idx: i })
    i += 1
  }

  flushMixed()
  return blocks
}

/** Single-browse samples: one table for all products (notes from text before each chip). */
function groupBlocksAsProductTable(segments: MessageSegment[]): MessageBlock[] {
  const blocks: MessageBlock[] = []
  const introParts: string[] = []
  const products: ProductHit[] = []
  let seenProduct = false
  let pendingNote = ''

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (seg.type === 'text') {
      if (!seenProduct) {
        introParts.push(seg.content)
      } else {
        pendingNote += seg.content
      }
      continue
    }

    seenProduct = true
    products.push({
      name: seg.name,
      product_id: seg.product_id,
      product_variant_color_id: seg.product_variant_color_id,
      note: glueNote(pendingNote),
      segmentIdx: i,
    })
    pendingNote = ''
  }

  const intro = introParts.join('').trim()
  if (intro) {
    blocks.push({
      type: 'mixed',
      segments: [{ type: 'text', content: intro, idx: 0 }],
    })
  }

  if (products.length > 0) {
    blocks.push({ type: 'table', products, kitSection: false })
  }

  const outro = pendingNote.trim()
  if (outro) {
    blocks.push({
      type: 'mixed',
      segments: [{ type: 'text', content: outro, idx: segments.length }],
    })
  }

  return blocks
}

function groupBlocks(segments: MessageSegment[]): MessageBlock[] {
  const hasKitHeaders = segments.some(
    (seg) => seg.type === 'text' && isKitSectionHeader(seg.content)
  )
  const hasProducts = segments.some((seg) => seg.type === 'product')

  if (!hasKitHeaders && hasProducts) {
    return groupBlocksAsProductTable(segments)
  }

  return groupBlocksWithKitSections(segments)
}

const isValidHttpsUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export const AssistantMessage = ({
  content,
  quote_id,
  i,
  onboardingAnchorHere = false,
}: {
  content: {
    message?: string
    references?: Array<{ url: string; label?: string }>
  }
  quote_id?: number
  i: number
  /** Only one message/chip may set the onboarding anchor id (see MessageList). */
  onboardingAnchorHere?: boolean
}) => {
  const { t } = useLocalization()
  const references = content?.references as
    | Array<{ url: string; label?: string }>
    | undefined
  const rawMessage =
    (typeof content?.message === 'string'
      ? content.message
      : typeof content === 'string'
      ? content
      : ''
    )?.replace(/\\n/g, '\n') ?? ''
  const segments = parseMessage(rawMessage)
  const blocks = groupBlocks(segments)
  const firstProductSegmentIdx = segments.findIndex((s) => s.type === 'product')
  const markdownComponents = {
    p: ({ children }: { children?: ReactNode }) => <p>{children}</p>,
    h2: ({ children }: { children?: ReactNode }) => <h2>{children}</h2>,
    h3: ({ children }: { children?: ReactNode }) => <h3>{children}</h3>,
    ul: ({ children }: { children?: ReactNode }) => <ul>{children}</ul>,
    li: ({ children }: { children?: ReactNode }) => <li>{children}</li>,
  }

  const chipId = (segmentIdx: number) =>
    onboardingAnchorHere && segmentIdx === firstProductSegmentIdx
      ? assistantOnboarding.elementId
      : undefined

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
    >
      <Column className="gap-2" key={i + 'assistant_message'}>
        {rawMessage && (
          <Column className="prose prose-sm max-w-none">
            {segments.some((s) => s.type === 'product') ? (
              blocks.map((block, blockIdx) => {
                if (block.type === 'table') {
                  const table = (
                    <Column
                      role="list"
                      className={cn(
                        'not-prose w-full overflow-hidden divide-y divide-border',
                        block.kitSection
                          ? 'rounded-b-xl border border-t-0 border-border bg-muted/20'
                          : 'my-2 rounded-xl border border-border bg-white'
                      )}
                    >
                      {block.products.map((product) => (
                        <InlineProductChip
                          key={product.segmentIdx}
                          id={chipId(product.segmentIdx)}
                          variant="row"
                          note={product.note}
                          product_id={product.product_id}
                          product_variant_color_id={
                            product.product_variant_color_id
                          }
                          name={product.name}
                          quote_id={quote_id ?? 0}
                        />
                      ))}
                    </Column>
                  )
                  return <div key={blockIdx}>{table}</div>
                }

                const hasProduct = block.segments.some(
                  (segment) => segment.type === 'product'
                )
                if (!hasProduct) {
                  const text = block.segments
                    .map((segment) =>
                      segment.type === 'text' ? segment.content : ''
                    )
                    .join('')
                  if (!text.trim()) return null
                  if (isKitSectionHeader(text)) {
                    return (
                      <div
                        key={blockIdx}
                        className="not-prose mt-4 first:mt-0 rounded-t-xl border border-border bg-muted/30 px-3 py-2 text-sm font-semibold text-foreground"
                      >
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <span>{children}</span>,
                            h3: ({ children }) => <span>{children}</span>,
                          }}
                        >
                          {t(text.trim())}
                        </ReactMarkdown>
                      </div>
                    )
                  }
                  return (
                    <ReactMarkdown
                      key={blockIdx}
                      components={markdownComponents}
                    >
                      {t(text)}
                    </ReactMarkdown>
                  )
                }

                return (
                  <p key={blockIdx} className="m-0 leading-relaxed">
                    {block.segments.map((segment) => {
                      if (segment.type === 'text') {
                        return (
                          <span key={segment.idx} className="inline">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => <span>{children}</span>,
                              }}
                            >
                              {t(segment.content)}
                            </ReactMarkdown>
                          </span>
                        )
                      }

                      return (
                        <InlineProductChip
                          key={segment.idx}
                          id={chipId(segment.idx)}
                          product_id={segment.product_id}
                          product_variant_color_id={
                            segment.product_variant_color_id
                          }
                          name={segment.name}
                          quote_id={quote_id ?? 0}
                        />
                      )
                    })}
                  </p>
                )
              })
            ) : (
              <ReactMarkdown components={markdownComponents}>
                {t(rawMessage)}
              </ReactMarkdown>
            )}
          </Column>
        )}

        {references && references.length > 0 && (
          <Row className="gap-2 flex-wrap mt-1.5">
            {references
              .filter((ref) => isValidHttpsUrl(ref.url))
              .map((ref, idx) => (
                <Badge variant={'outline'} className="hover:bg-gray-100">
                  <a
                    key={idx}
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5"
                    aria-label={ref.label || 'Open reference'}
                  >
                    <Link2 className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate max-w-[180px] text-xs">
                      {ref.label || ref.url}
                    </span>
                  </a>
                </Badge>
              ))}
          </Row>
        )}
      </Column>
    </motion.div>
  )
}
